<template>
  <OmtGrid>
    <OmtRow v-if="!!refundDetails.protocol">
      <OmtTypography as="h1-bold" mb="24px"> Protocolo {{ refundDetails.protocol }} </OmtTypography>
    </OmtRow>

    <OmtRow mb="14px">
      <CFlex width="100%" :flexDirection="['column', 'row']" gap="16px">
        <CBox flex="1">
          <TextTile v-bind="{ title: 'Data da solicitação', description: refundDetails.createdAt }" />
        </CBox>

        <CBox flex="1" v-if="refundDetails.showValues">
          <TextTile v-bind="{ title: 'Valor total apresentado', description: refundDetails.totalAmount }" />
        </CBox>

        <CBox flex="1">
          <TextTile v-bind="{ title: 'Valor total reembolsado', description: refundedTotalDescription }" />
        </CBox>
      </CFlex>
    </OmtRow>

    <OmtRow :mobileStart="3" :desktopStart="6" v-if="refundDetails.showValues">
      <TextTile v-bind="{ title: 'Valor total apresentado', description: refundDetails.totalAmount }" />
    </OmtRow>

    <!-- Seção de conta corrente para crédito -->
    <OmtRow mb="24px" mt="10px">
      <CBox w="100%" p="16px" border="0.5px solid #DADADA" borderRadius="8px">
        <CFlex cursor="pointer" @click="() => (isOpenAccount = !isOpenAccount)" justify-content="space-between">
          <OmtTypography as="p1"> Conta Corrente para Crédito </OmtTypography>
          <OmtIcons
            ml="16px"
            name="chevron-down"
            :style="`transition: all .5s ease; transform: rotate(${isOpenAccount ? '180deg' : '0deg'})`"
          />
        </CFlex>
        <CCollapse :isOpen="isOpenAccount">
          <CDivider mt="16px" mb="16px" />
          <OmtRow :desktopEnd="5" mb="14px">
            <TextTile v-bind="{ title: 'Favorecido', description: refundDetails.accountDetails.name }" />
          </OmtRow>
          <OmtRow :mobileEnd="2" :desktopStart="6" :desktopEnd="8" mb="14px">
            <TextTile v-bind="{ title: 'Conta Corrente', description: refundDetails.accountDetails.account }" />
            <TextTile v-bind="{ title: 'Agência', description: refundDetails.accountDetails.agency }" />
          </OmtRow>

          <OmtRow mt="16px">
            <TextTile v-bind="{ title: 'Banco', description: refundDetails.accountDetails.bank }" />
          </OmtRow>
        </CCollapse>
      </CBox>
    </OmtRow>

    <OmtRow>
      <OmtTypography as="h2-bold" mb="24px" data-cy="refund-detail-docs-label">
        {{ refundDetails.docsLabel }}
      </OmtTypography>
    </OmtRow>

    <OmtRow v-for="(refund, index) in refundDetails.refunds" mb="24px" :key="`stat-${index}`">
      <RefundDetailCollapsableCard
        :refundDetail="refund"
        :cancelPendency="openCancellationModal"
        :isCollapsed="true"
        :complementaryPictures="pictures"
      />
      <AuditFormCancellationModal :isOpen.sync="showCancellationModal" @cancel="handleCancel" />
    </OmtRow>

    <!-- Botão para download do demonstrativo -->
    <template v-if="hasDemonstrativo">
      <OmtButton mb="24px" height="40px" width="223px" @click="downloadDemonstrativorativo()">
        <OmtIcons size="1x" name="arrow-down-to-bracket" color="white" mr="8px"></OmtIcons>
        <OmtTypography as="h3-bold" color="#FFFFF">Detalhes do pagamento</OmtTypography>
      </OmtButton>
    </template>

    <!-- Seção de observações -->
    <OmtRow mb="20px">
      <OmtCard>
        <template v-if="refundDetails.obsHistory && refundDetails.obsHistory.length">
          <CBox v-for="(obs, index) in refundDetails.obsHistory" :key="`obs-${index}`">
            <CFlex align-items="center" justify-content="space-between" mb="8px">
              <CFlex align-items="center" gap="16px">
                <CBox
                  width="24px"
                  height="24px"
                  bg="var(--colors-light-support-news)"
                  border-radius="50%"
                  display="flex"
                  align-items="center"
                  justify-content="center"
                  mr="16px"
                >
                  <OmtIcons name="user" size="xs" color="white" />
                </CBox>
                <OmtTypography as="h3-bold" color="var(--colors-light-primary-gray1)">Sua solicitação</OmtTypography>
              </CFlex>
              <OmtTypography v-if="obs.Data" as="p2" color="#8C8C8C">{{ obs.Data }}</OmtTypography>
            </CFlex>
            <CFlex align-items="center" gap="29px" pl="40px">
              <CBox>
                <OmtTypography
                  v-for="(obsLine, obsIndex) in obs.Obs"
                  :key="`obs-lin-${index}-${obsIndex}`"
                  as="p1"
                  color="var(--colors-light-primary-gray1)"
                >
                  {{ obsLine }}
                </OmtTypography>
              </CBox>
            </CFlex>
          </CBox>
        </template>
        <template v-else>
          <OmtTypography as="p1" mb="8px" color="var(--colors-light-primary-gray1)">{{
            refundDetails.obsProtocol
          }}</OmtTypography>
        </template>
      </OmtCard>
    </OmtRow>

    <OmtRow>
      <AnswerPendencyModal
        :isOpen.sync="showPendencyModal"
        :pendencyToAnswer.sync="pendencyToAnswer"
        :protocol="protocolCreatedOnSave"
        :step="pendencyModalStep"
        :limitFiles="limitFiles"
        @savePendency="onSavePendency"
        @closeProtocolModal="onCloseProtocolModal"
        filesDescription="Insira somente a(s) imagem(ns) e/ou informações solicitadas e relacionadas a este(s) documento(s)"
        protocolMainTip="Você será notificado sobre o andamento desta solicitação via WhatsApp"
        protocolSubTip="Você poderá acompanhar o status na tela de Reembolsos"
      />
    </OmtRow>

    <FixedButtonRow v-if="pendencyContext">
      <OmtButton
        data-cy="answer-pendencies-button"
        :w="['100%', '308px !important']"
        height="40px"
        @click="handleFloatingPendencyClick"
      >
        <OmtTypography as="button-small" color="white">{{
          pendencyContext.refundDetail.status === "P" ? "Responder pendências" : "Responder auditoria"
        }}</OmtTypography>
      </OmtButton>
    </FixedButtonRow>
  </OmtGrid>
</template>

<script>
import FixedButtonRow from "@/components/Atoms/FixedButtonRow/index.vue";
import AnswerPendencyModal from "@/components/Molecules/AnswerPendencyModal/index.vue";
import ImagePreviewer from "@/components/Molecules/ImagePreviewer";
import TextTile from "@/components/Molecules/TextTile";
import AuditFormCancellationModal from "@/components/Organisms/RefundNew/AuditForm/AuditFormCancellationModal.vue";
import RefundDetailCollapsableCard from "@/components/Organisms/RefundNew/RefundDetailCollapsableCard.vue";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { mapActions } from "vuex";

export default {
  components: {
    AnswerPendencyModal,
    RefundDetailCollapsableCard,
    TextTile,
    ImagePreviewer,
    AuditFormCancellationModal,
    FixedButtonRow
  },
  async mounted() {
    if (!this.protocol) {
      this.$router.push({ name: "refund.new.list" });
    }
    try {
      await this.$root.$emit("show-loading");
      this.refundDetails = await this.fetchProtocolDetail({ protocol: this.protocol });
      if (!this.refundDetails) {
        this.$router.push({ name: "refund.new.list" });
      }
      this.limitFiles = await this.fetchMaxFiles();
      this.loadPictures();
    } catch {
      // TODO confirmar se vai exibir algum aviso
      this.$router.push({ name: "refund.new.list" });
    } finally {
      await this.$root.$emit("hide-loading");
    }
  },
  data() {
    return {
      cancellationTicket: "",
      limitFiles: 0,
      showCancellationModal: false,
      showPendencyModal: false,
      refundDetails: { accountDetails: {}, refunds: [] },
      pictures: [],
      pendencyToAnswer: {},
      pendencyModalStep: 1,
      protocolCreatedOnSave: "",
      isOpenAccount: false
    };
  },
  computed: {
    refundedTotalDescription() {
      if (!this.refundDetails) return "-";
      const value = this.refundDetails.refundedTotal;
      return value ? value : "-";
    },
    protocol() {
      return this.$route.params.protocolo ?? "";
    },
    hasDemonstrativo() {
      return this.refundDetails?.refunds?.some(
        all => all.list?.some(ticket => ticket.docs?.some(doc => doc.demonstrativo !== null)) || false
      );
    },
    pendencyContext() {
      const refunds = this.refundDetails?.refunds || [];
      for (const refund of refunds) {
        if (!["P", "U"].includes(refund.status)) continue;
        const tickets = refund.list || [];
        const firstTicket = tickets.find(t => !!t);
        if (firstTicket) {
          return { ticket: firstTicket, refundDetail: refund };
        }
      }
      return null;
    }
  },
  methods: {
    ...mapActions("refund", [
      "fetchMaxFiles",
      "fetchProtocolDetail",
      "fetchProtocolDetailImage",
      "returnRefundTicket",
      "fetchProtocols",
      "cancelAudit"
    ]),
    ...mapActions("saude", ["fetchProfileMessages"]),
    loadPictures() {
      if (!this.refundDetails?.pictures?.length) return;

      this.refundDetails.pictures.forEach(p =>
        this.fetchProtocolDetailImage({ documento: p.id, original: false }).then(source =>
          this.pictures.push({
            id: p.id,
            source,
            loadModalImage: () => this.loadPicture(p.id)
          })
        )
      );
    },
    async loadPicture(id) {
      return await this.fetchProtocolDetailImage({ documento: id, original: true });
    },
    async onSavePendency(data) {
      await this.$root.$emit("show-loading");
      this.showPendencyModal = false;

      let newRefund = {};

      try {
        newRefund = await this.returnRefundTicket(data);

        if (newRefund && newRefund.Data) {
          await this.fetchProfileMessages();
          await this.fetchProtocols({ protocol: newRefund.Data.protocoloANS });
        }
      } catch (error) {
        console.error(error.message);
      } finally {
        await this.$root.$emit("hide-loading");
      }

      if (newRefund && newRefund.Data) {
        this.protocolCreatedOnSave = newRefund.Data.protocoloANS;
        this.pendencyModalStep = 2;
        this.showPendencyModal = true;
      } else {
        await Swal.fire({
          title: "Erro",
          text: "Não foi possivel registrar o atendimento.\nServiço temporariamente indisponível.",
          confirmButtonText: "OK",
          icon: "error"
        });
      }
    },
    onCloseProtocolModal() {
      this.$toast({
        description: "Sua resposta foi enviada para análise",
        status: "success",
        duration: 10000,
        position: "top"
      });
      this.$root.$emit("show-loading");
      this.$router.replace({ name: "refund.new.list" });
    },
    answerPendency({ ticket, refundDetail }) {
      if (refundDetail.status === "P") {
        this.showPendencyModal = true;
        this.pendencyToAnswer = ticket;
      } else if (refundDetail.status === "U") {
        const { ticket: tickerNumber, dueDate } = ticket;

        this.$router.push({
          name: "refund.new.audit.explanation",
          params: {
            dueDate,
            ticket: tickerNumber,
            refund: this.refundDetails
          }
        });
      }
    },
    openCancellationModal({ ticket }) {
      this.cancellationTicket = ticket;
      this.showCancellationModal = true;
    },
    handleCancel() {
      this.$router.push({
        name: "refund.new.audit.term",
        params: {
          scenario: 6,
          answers: [],
          refund: this.refundDetails,
          ticket: this.cancellationTicket,
          mustCancelProtocol: true
        }
      });
    },
    downloadDemonstrativorativo() {
      if (!this.hasDemonstrativo) {
        return;
      }
      const links = this.extractDemonstrativoLinks();
      if (links && links.length) {
        window.location = links[0];
      }
    },
    extractDemonstrativoLinks() {
      return this.refundDetails?.refunds
        .flatMap(all => all?.list)
        .flatMap(ticket => ticket?.docs)
        .flatMap(doc => doc?.demonstrativo)
        .filter(demonstrativo => demonstrativo !== null && demonstrativo !== undefined)
        .map(demonstrativo => demonstrativo.source);
    },
    handleFloatingPendencyClick() {
      if (!this.pendencyContext) return;
      this.answerPendency(this.pendencyContext);
    }
  }
};
</script>

<style scoped>
::v-deep .swal2-container {
  padding: 0;
}

::v-deep .swal2-popup {
  height: 100vh;
  width: 100vw;
  display: flex;
}

::v-deep .swal2-image {
  height: 80vh;
  width: 100%;
  object-fit: fill;
}
</style>
